import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import ManageClientAccountCampaignSelection from 'src/pages/client-management/components/manage-clients/manage-client-account-campaign-selection';
import { useGetAccountsQuery, useGetCampaignsQuery } from 'src/pages/client-management/endpoints/accounts.endpoints';
import { useGetOrganizationClientsQuery } from 'src/pages/client-management/endpoints/client.endpoints';
import type { Account, Campaign } from 'src/pages/client-management/types/client-management.types';
import type { Client } from 'src/pages/client-management/types/client.types';
import {
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { allowsCampaignEmailDailyFrequency } from 'src/helpers/env.helpers';
import type { CampaignEmailFormValues, CampaignEmailSchedule } from '../types/campaign-email.types';
import {
  CAMPAIGN_EMAIL_DAY_OF_WEEK_OPTIONS,
  getCampaignEmailFrequencyOptions,
  EMPTY_CAMPAIGN_EMAIL_FORM_VALUES,
} from '../utils/campaign-email.constants';
import {
  formValuesToWritePayload,
  getClientPortalUsers,
  scheduleToFormValues,
} from '../utils/campaign-email.mappers';
import {
  hasCampaignEmailFormErrors,
  validateCampaignEmailForm,
  type CampaignEmailFormErrors,
} from '../utils/campaign-email.validation';
import { CampaignEmailContentSection } from './campaign-email-content-section';
import { CampaignEmailRecipientsPicker } from './campaign-email-recipients-picker';

interface CampaignEmailScheduleFormProps {
  initialSchedule?: CampaignEmailSchedule | null;
  isSaving?: boolean;
  saveError?: string | null;
  onSubmit: (values: CampaignEmailFormValues) => void;
  onCancel: () => void;
  onPreview?: (values: CampaignEmailFormValues) => void;
  onTestSend?: (values: CampaignEmailFormValues) => void;
  isPreviewLoading?: boolean;
  isTestSendLoading?: boolean;
}

function campaignsWithAccount(campaigns: Campaign[]): Campaign[] {
  return campaigns.filter((campaign) => Boolean(campaign?.account?.id && campaign.account.source));
}

function filterCampaignsForClient(client: Client | undefined, campaigns: Campaign[]): Campaign[] {
  const usableCampaigns = campaignsWithAccount(campaigns);
  if (!client?.access?.length) {
    return usableCampaigns;
  }

  const allowedKeys = new Set(
    client.access.map((access) => `${access.source}:${access.campaign}`)
  );

  return usableCampaigns.filter((campaign) => (
    allowedKeys.has(`${campaign.account.source}:${campaign.ext_campaign_id}`)
    || allowedKeys.has(`${campaign.account.source}:${campaign.id}`)
  ));
}

function accountsForCampaigns(accounts: Account[], campaigns: Campaign[]): Account[] {
  const accountIds = new Set(
    campaigns
      .map((campaign) => campaign.account?.id)
      .filter((accountId): accountId is string => Boolean(accountId))
  );
  return accounts.filter((account) => accountIds.has(account.id));
}

export function CampaignEmailScheduleForm({
  initialSchedule = null,
  isSaving = false,
  saveError = null,
  onSubmit,
  onCancel,
  onPreview,
  onTestSend,
  isPreviewLoading = false,
  isTestSendLoading = false,
}: CampaignEmailScheduleFormProps) {
  const { data: clients = [] } = useGetOrganizationClientsQuery();
  const { data: accountsPage } = useGetAccountsQuery();
  const { data: campaignsPage } = useGetCampaignsQuery();

  const accounts = accountsPage?.items ?? [];
  const allCampaigns = campaignsPage?.items ?? [];

  const [values, setValues] = useState<CampaignEmailFormValues>(EMPTY_CAMPAIGN_EMAIL_FORM_VALUES);
  const [errors, setErrors] = useState<CampaignEmailFormErrors>({});
  const [searchValue, setSearchValue] = useState('');
  const frequencyOptions = useMemo(
    () => getCampaignEmailFrequencyOptions(allowsCampaignEmailDailyFrequency()),
    [],
  );

  useEffect(() => {
    if (initialSchedule) {
      setValues(scheduleToFormValues(initialSchedule, allCampaigns));
      return;
    }
    setValues(EMPTY_CAMPAIGN_EMAIL_FORM_VALUES);
  }, [initialSchedule, allCampaigns]);

  const selectedClient = useMemo(
    () => clients.find((client) => client.ext_client_id === values.clientId),
    [clients, values.clientId]
  );

  const visibleCampaigns = useMemo(
    () => filterCampaignsForClient(selectedClient, allCampaigns),
    [selectedClient, allCampaigns]
  );

  const visibleAccounts = useMemo(
    () => accountsForCampaigns(accounts, visibleCampaigns),
    [accounts, visibleCampaigns]
  );

  // Selection tree keys are platform ext IDs; form state stores internal UUIDs.
  const selectedCampaignExtIds = useMemo(
    () => visibleCampaigns
      .filter((campaign) => values.selectedCampaignIds.includes(campaign.id))
      .map((campaign) => campaign.ext_campaign_id),
    [visibleCampaigns, values.selectedCampaignIds]
  );

  const portalUsers = useMemo(
    () => getClientPortalUsers(selectedClient),
    [selectedClient]
  );

  const updateValues = (patch: Partial<CampaignEmailFormValues>) => {
    setValues((current) => {
      const next = { ...current, ...patch };
      if (patch.frequency && patch.frequency !== 'daily') {
        next.startDate = '';
        next.endDate = '';
      }
      return next;
    });
    setErrors({});
  };

  const handleClientChange = (clientId: string) => {
    updateValues({
      clientId,
      selectedCampaignIds: [],
      portalContactIds: [],
    });
  };

  const handleSelectionChange = (accountIds: string[], campaignExtIds: string[]) => {
    void accountIds;
    const nextCampaignIds = visibleCampaigns
      .filter((campaign) => campaignExtIds.includes(campaign.ext_campaign_id))
      .map((campaign) => campaign.id);
    updateValues({ selectedCampaignIds: nextCampaignIds });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateCampaignEmailForm(values);
    setErrors(nextErrors);
    if (hasCampaignEmailFormErrors(nextErrors)) {
      return;
    }
    onSubmit(values);
  };

  const handleTestSend = () => {
    const nextErrors = validateCampaignEmailForm(values);
    setErrors(nextErrors);
    if (hasCampaignEmailFormErrors(nextErrors) || !onTestSend) {
      return;
    }
    onTestSend(values);
  };

  const handlePreview = () => {
    const nextErrors = validateCampaignEmailForm(values);
    setErrors(nextErrors);
    if (hasCampaignEmailFormErrors(nextErrors) || !onPreview) {
      return;
    }
    onPreview(values);
  };

  const clientBrandUrl = selectedClient?.brand?.trim() || null;
  const overrideBrandUrl = values.useBrandingOverride
    ? values.brandingOverride.trim() || null
    : null;
  const brandingPreviewUrl = overrideBrandUrl || clientBrandUrl;
  const brandingHint = brandingPreviewUrl
    ? (overrideBrandUrl
      ? 'Custom logo will be used in the email header.'
      : 'Defaults to the client logo in the email header.')
    : 'No client logo set; provide a branding URL to show a logo in the email.';

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <PortalDsCard>
        <PortalDsSectionHeader
          title={initialSchedule ? 'Edit email schedule' : 'Create email schedule'}
          description="Configure automated campaign performance emails."
        />

        <div className="grid gap-5 lg:grid-cols-2">
          <PortalDsField
            label="Schedule name"
            required
            error={errors.name}
          >
            <PortalDsInput
              value={values.name}
              disabled={isSaving}
              placeholder="Weekly performance summary"
              onChange={(event) => updateValues({ name: event.target.value })}
            />
          </PortalDsField>

          <PortalDsField
            label="Client"
            error={errors.clientId}
            hint="Optional. Selecting a client autopopulates campaigns, portal contacts, and branding."
          >
            <select
              className={portalDs.select}
              value={values.clientId}
              disabled={isSaving}
              onChange={(event) => handleClientChange(event.target.value)}
            >
              <option value="">
                No client (optional)
              </option>
              {clients.map((client) => (
                <option
                  key={client.ext_client_id}
                  value={client.ext_client_id}
                >
                  {client.title}
                </option>
              ))}
            </select>
          </PortalDsField>

          <PortalDsField label="Frequency">
            <select
              className={portalDs.select}
              value={values.frequency}
              disabled={isSaving}
              onChange={(event) => updateValues({
                frequency: event.target.value as CampaignEmailFormValues['frequency'],
              })}
            >
              {frequencyOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </PortalDsField>

          <PortalDsField
            label="Day of the week"
            required
            error={errors.dayOfWeek}
            hint="Emails send on this weekday for weekly, bi-weekly, and monthly schedules."
          >
            <select
              className={portalDs.select}
              value={values.dayOfWeek}
              disabled={isSaving}
              onChange={(event) => updateValues({
                dayOfWeek: Number(event.target.value) as CampaignEmailFormValues['dayOfWeek'],
              })}
            >
              {CAMPAIGN_EMAIL_DAY_OF_WEEK_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </PortalDsField>

          {values.frequency === 'daily' ? (
            <>
              <PortalDsField
                label="Start date"
                required
                error={errors.startDate}
                hint="First day this daily test schedule may send."
              >
                <PortalDsInput
                  type="date"
                  value={values.startDate}
                  disabled={isSaving}
                  onChange={(event) => updateValues({ startDate: event.target.value })}
                />
              </PortalDsField>

              <PortalDsField
                label="End date"
                required
                error={errors.endDate}
                hint="Last day this daily test schedule may send."
              >
                <PortalDsInput
                  type="date"
                  value={values.endDate}
                  disabled={isSaving}
                  onChange={(event) => updateValues({ endDate: event.target.value })}
                />
              </PortalDsField>
            </>
          ) : null}
        </div>
      </PortalDsCard>

      <PortalDsCard>
        <PortalDsSectionHeader
          title="Campaigns"
          description="Select the accounts and campaigns included in this email."
        />

        {values.clientId ? null : (
          <p className={cn(portalDs.caption, 'mb-4')}>
            No client selected — showing all campaigns. Choose a client above to narrow this list.
          </p>
        )}

        <PortalDsField
          label="Search campaigns"
          filter
          className="mb-4 max-w-md"
        >
          <PortalDsInput
            value={searchValue}
            disabled={isSaving}
            placeholder="Filter campaigns"
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </PortalDsField>

        <ManageClientAccountCampaignSelection
          accounts={visibleAccounts}
          campaigns={visibleCampaigns}
          selectedAccountIds={[]}
          selectedCampaignIds={selectedCampaignExtIds}
          onSelectionChange={handleSelectionChange}
          searchValue={searchValue}
          disabled={isSaving}
        />

        {errors.selectedCampaignIds ? (
          <p
            className="mt-3 text-xs text-[#DC2626]"
            role="alert"
          >
            {errors.selectedCampaignIds}
          </p>
        ) : null}
      </PortalDsCard>

      <PortalDsCard>
        <PortalDsSectionHeader
          title="Recipients"
          description="Portal contacts and manual email addresses can be combined on one schedule."
        />
        <CampaignEmailRecipientsPicker
          portalUsers={portalUsers}
          selectedPortalContactIds={values.portalContactIds}
          manualRecipients={values.manualRecipients}
          onPortalContactsChange={(portalContactIds) => updateValues({ portalContactIds })}
          onManualRecipientsChange={(manualRecipients) => updateValues({ manualRecipients })}
          error={errors.recipients}
          manualError={errors.manualRecipients}
          disabled={isSaving}
          portalContactsDisabled={!values.clientId}
        />
      </PortalDsCard>

      <PortalDsCard>
        <PortalDsSectionHeader
          title="Email content"
          description="Choose metrics, charts, and optional AI narrative settings."
        />
        <CampaignEmailContentSection
          sectionsConfig={values.sectionsConfig}
          aiNarrativeEnabled={values.aiNarrativeEnabled}
          customPromptInstructions={values.customPromptInstructions}
          onSectionsConfigChange={(sectionsConfig) => updateValues({ sectionsConfig })}
          onAiNarrativeEnabledChange={(aiNarrativeEnabled) => updateValues({ aiNarrativeEnabled })}
          onCustomPromptInstructionsChange={(customPromptInstructions) => (
            updateValues({ customPromptInstructions })
          )}
          sectionsError={errors.sectionsConfig}
          customPromptError={errors.customPromptInstructions}
          disabled={isSaving}
        />
      </PortalDsCard>

      <PortalDsCard>
        <PortalDsSectionHeader
          title="Branding"
          description={brandingHint}
        />

        <div className="space-y-4">
          {brandingPreviewUrl ? (
            <div className="flex items-center gap-3">
              <img
                src={brandingPreviewUrl}
                alt={selectedClient?.title ? `${selectedClient.title} logo` : 'Brand logo'}
                className="h-12 w-auto max-w-[160px] object-contain"
              />
              <span className={portalDs.caption}>
                {overrideBrandUrl ? 'Custom logo preview' : 'Client logo'}
              </span>
            </div>
          ) : null}

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={values.useBrandingOverride}
              disabled={isSaving}
              onChange={(event) => updateValues({ useBrandingOverride: event.target.checked })}
              className="mt-1 size-4 rounded border-[#D1D5DB]"
            />
            <span>
              <span className="block text-[13px] font-medium text-[#0D0F1A]">
                Override client branding
              </span>
              <span className={portalDs.caption}>
                Use a custom logo URL instead of the client logo.
              </span>
            </span>
          </label>

          {values.useBrandingOverride ? (
            <PortalDsField
              label="Branding URL"
              error={errors.brandingOverride}
            >
              <PortalDsInput
                type="url"
                value={values.brandingOverride}
                disabled={isSaving}
                placeholder="https://example.com/logo.png"
                onChange={(event) => updateValues({ brandingOverride: event.target.value })}
              />
            </PortalDsField>
          ) : null}
        </div>
      </PortalDsCard>

      {saveError ? (
        <p
          className={cn(portalDs.bodySm, 'text-[#DC2626]')}
          role="alert"
        >
          {saveError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        <PortalDsButton
          type="button"
          variant="ghost"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </PortalDsButton>
        {onPreview ? (
          <PortalDsButton
            type="button"
            variant="secondary"
            disabled={isSaving || isPreviewLoading || isTestSendLoading}
            onClick={handlePreview}
          >
            {isPreviewLoading ? (
              <>
                <Loader2
                  className="size-4 animate-spin"
                  aria-hidden
                />
                Previewing…
              </>
            ) : 'Preview email'}
          </PortalDsButton>
        ) : null}
        {onTestSend ? (
          <PortalDsButton
            type="button"
            variant="secondary"
            disabled={isSaving || isPreviewLoading || isTestSendLoading}
            onClick={handleTestSend}
          >
            {isTestSendLoading ? (
              <>
                <Loader2
                  className="size-4 animate-spin"
                  aria-hidden
                />
                Preparing…
              </>
            ) : 'Send test email'}
          </PortalDsButton>
        ) : null}
        <PortalDsButton
          type="submit"
          variant="primary"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2
                className="size-4 animate-spin"
                aria-hidden
              />
              Saving…
            </>
          ) : initialSchedule ? 'Save changes' : 'Create schedule'}
        </PortalDsButton>
      </div>
    </form>
  );
}

export function buildSchedulePayloadFromForm(
  values: CampaignEmailFormValues,
  campaigns: Campaign[]
) {
  return formValuesToWritePayload(values, campaigns);
}
